/**
 * Converts markdown to clean HTML
 * FOCUSED: Headers, numbered lists, bold, italics - preserves ALL content
 * @param {string} markdown - Markdown text
 * @returns {string} - HTML string
 */
export function markdownToHTML(markdown) {
  if (!markdown || typeof markdown !== 'string') {
    return '';
  }

  let html = markdown;

  // Step 1: Convert numbered lists (must be at start of line)
  // Pattern: "1. ", "2. ", etc. or "1) ", "2) ", etc.
  // Process line by line to handle numbered lists properly
  const lines = html.split('\n');
  let inOrderedList = false;
  let processedLines = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const numberedMatch = line.match(/^(\d+)[\.\)]\s+(.+)$/);
    
    if (numberedMatch) {
      // Start ordered list if not already in one
      if (!inOrderedList) {
        processedLines.push('<ol>');
        inOrderedList = true;
      }
      processedLines.push(`<li>${numberedMatch[2]}</li>`);
    } else {
      // End ordered list if we were in one
      if (inOrderedList) {
        processedLines.push('</ol>');
        inOrderedList = false;
      }
      processedLines.push(line);
    }
  }
  
  // Close any open list
  if (inOrderedList) {
    processedLines.push('</ol>');
  }
  
  html = processedLines.join('\n');

  // Step 2: Convert headers (must be at start of line after list processing)
  html = html.replace(/^###### (.*)$/gm, '<h6>$1</h6>');
  html = html.replace(/^##### (.*)$/gm, '<h5>$1</h5>');
  html = html.replace(/^#### (.*)$/gm, '<h4>$1</h4>');
  html = html.replace(/^### (.*)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.*)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.*)$/gm, '<h1>$1</h1>');

  // Step 3: Convert inline formatting (bold, italic)
  // Process bold first (double asterisks), then italic (single asterisks)
  // Use non-greedy matching to handle multiple instances on same line
  // IMPORTANT: Process bold before italic to avoid conflicts
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  // Then process single asterisks for italic (avoid matching inside <strong> tags)
  // Match single * that's not part of ** and not inside HTML tags
  html = html.replace(/\*([^*]+?)\*/g, (match, content) => {
    // Don't convert if it's inside a tag we just created
    if (match.includes('<strong>') || match.includes('</strong>')) {
      return match;
    }
    return '<em>' + content + '</em>';
  });
  // Also handle underscores for italics (simple pattern, avoid conflicts)
  html = html.replace(/_([^_]+?)_/g, (match, content) => {
    // Don't convert if it's inside a tag we just created
    if (match.includes('<em>') || match.includes('</em>') || match.includes('<strong>')) {
      return match;
    }
    return '<em>' + content + '</em>';
  });

  // Step 4: Convert line breaks to <br> (but preserve list structure)
  // Don't add <br> after list items, headers, or closing tags
  // First, protect existing HTML tags from line break insertion
  html = html.replace(/\n/g, (match, offset, string) => {
    // Check if next character starts an HTML tag we created
    const afterMatch = string.substring(offset + match.length);
    if (afterMatch.match(/^<[\/]?(h[1-6]|ol|li|strong|em)/)) {
      return '\n'; // Keep newline, don't convert to <br>
    }
    // Check if previous character ends an HTML tag we created
    const beforeMatch = string.substring(0, offset);
    if (beforeMatch.match(/<\/(h[1-6]|ol|li|strong|em)>$/)) {
      return '\n'; // Keep newline, don't convert to <br>
    }
    return '<br>';
  });
  
  // Step 5: Escape HTML entities in text content only (not in our HTML tags)
  // Split by HTML tags we created, escape text parts
  const tagPattern = /(<\/?(?:h[1-6]|ol|li|strong|em|br)(?:\s[^>]*)?>)/gi;
  const parts = html.split(tagPattern);
  
  html = parts.map(part => {
    // If it's an HTML tag we created, keep it as-is
    // Use a fresh regex test for each part to avoid regex state issues
    const isTag = /^<\/?(?:h[1-6]|ol|li|strong|em|br)(?:\s[^>]*)?>$/i.test(part);
    if (isTag) {
      return part;
    }
    // Otherwise escape HTML entities in text content
    // But don't double-escape already escaped entities
    // IMPORTANT: Only escape if it's not already part of an HTML tag
    if (part.trim().length === 0) {
      return part; // Preserve whitespace
    }
    return part
      .replace(/&(?!amp;|lt;|gt;|quot;|#\d+;)/g, '&amp;') // Don't double-escape
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }).join('');

  // Step 6: Clean up excessive breaks and empty tags
  html = html.replace(/<br>\s*<br>\s*<br>+/g, '<br><br>'); // Max 2 consecutive breaks
  html = html.replace(/<br>\s*<\/ol>/g, '</ol>');
  html = html.replace(/<br>\s*<\/h[1-6]>/g, (match) => {
    return match.replace(/<br>\s*/, '');
  });

  return html.trim();
}
